import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Users,
  ChartLine,
  SignOut,
  Plus,
  MagnifyingGlass,
  Funnel,
  CaretRight,
  CurrencyDollar,
  Target,
  Trophy,
  Clock
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  lead: 'bg-gray-100 text-gray-700',
  qualified: 'bg-blue-100 text-blue-700',
  demo_scheduled: 'bg-purple-100 text-purple-700',
  proposal: 'bg-orange-100 text-orange-700',
  closed_won: 'bg-green-100 text-green-700',
  closed_lost: 'bg-red-100 text-red-700'
};

const statusLabels = {
  lead: 'Lead',
  qualified: 'Qualified',
  demo_scheduled: 'Demo Scheduled',
  proposal: 'Proposal',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost'
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [statsRes, customersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/customers${statusFilter ? `?status=${statusFilter}` : ''}`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setCustomers(customersRes.data);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-[#555555]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
              <span className="text-white font-heading font-bold">FR</span>
            </div>
            <div>
              <h1 className="font-heading font-semibold text-lg">FRG Admin</h1>
              <p className="text-xs text-[#555555]">CRM Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#555555]">{user?.name || user?.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#555555] hover:text-[#111111] transition-colors"
              data-testid="logout-btn"
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-[#E5E5E5] p-6" data-testid="stat-total-customers">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
                <Users size={20} className="text-[#002FA7]" />
              </div>
              <span className="text-sm text-[#555555]">Total Customers</span>
            </div>
            <p className="font-heading font-bold text-3xl text-[#111111]">{stats?.total_customers || 0}</p>
          </div>

          <div className="bg-white border border-[#E5E5E5] p-6" data-testid="stat-pipeline-value">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 flex items-center justify-center">
                <Target size={20} className="text-purple-600" />
              </div>
              <span className="text-sm text-[#555555]">Pipeline Value</span>
            </div>
            <p className="font-heading font-bold text-3xl text-[#111111]">{formatCurrency(stats?.pipeline_value)}</p>
          </div>

          <div className="bg-white border border-[#E5E5E5] p-6" data-testid="stat-won-value">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 flex items-center justify-center">
                <Trophy size={20} className="text-green-600" />
              </div>
              <span className="text-sm text-[#555555]">Won Revenue</span>
            </div>
            <p className="font-heading font-bold text-3xl text-[#111111]">{formatCurrency(stats?.won_value)}</p>
          </div>

          <div className="bg-white border border-[#E5E5E5] p-6" data-testid="stat-active-deals">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-orange-100 flex items-center justify-center">
                <Clock size={20} className="text-orange-600" />
              </div>
              <span className="text-sm text-[#555555]">Active Deals</span>
            </div>
            <p className="font-heading font-bold text-3xl text-[#111111]">
              {(stats?.pipeline_counts?.qualified || 0) + (stats?.pipeline_counts?.demo_scheduled || 0) + (stats?.pipeline_counts?.proposal || 0)}
            </p>
          </div>
        </div>

        {/* Pipeline Overview */}
        <div className="bg-white border border-[#E5E5E5] p-6 mb-8">
          <h2 className="font-heading font-semibold text-lg mb-4">Sales Pipeline</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(statusLabels).map(([status, label]) => (
              <button
                key={status}
                onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
                className={`p-4 border text-center transition-colors ${statusFilter === status ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-[#E5E5E5] hover:border-[#002FA7]'}`}
                data-testid={`pipeline-${status}`}
              >
                <p className="font-heading font-bold text-2xl text-[#111111]">{stats?.pipeline_counts?.[status] || 0}</p>
                <p className="text-xs text-[#555555] mt-1">{label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white border border-[#E5E5E5]">
          <div className="p-6 border-b border-[#E5E5E5] flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h2 className="font-heading font-semibold text-lg">Customers</h2>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-[#E5E5E5] w-full sm:w-64 focus:outline-none focus:border-[#002FA7]"
                  data-testid="customer-search"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-[#E5E5E5] focus:outline-none focus:border-[#002FA7]"
                data-testid="status-filter"
              >
                <option value="">All Statuses</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>

              <button
                onClick={() => setShowNewModal(true)}
                className="bg-[#002FA7] text-white px-4 py-2 flex items-center gap-2 hover:bg-[#001D66] transition-colors"
                data-testid="add-customer-btn"
              >
                <Plus size={18} />
                Add Customer
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="customers-table">
              <thead className="bg-[#F3F3F3]">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[#555555] uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[#555555] uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[#555555] uppercase tracking-wider">Product</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[#555555] uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[#555555] uppercase tracking-wider">Deal Value</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-[#555555] uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-[#555555]">
                      No customers found
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-[#FAFAFA] cursor-pointer" onClick={() => navigate(`/admin/customers/${customer.id}`)}>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-[#111111]">{customer.name}</p>
                          <p className="text-sm text-[#555555]">{customer.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[#555555]">{customer.company || '-'}</td>
                      <td className="px-6 py-4 text-[#555555]">{customer.product_interest || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-2 py-1 text-xs font-medium ${statusColors[customer.status] || 'bg-gray-100 text-gray-700'}`}>
                          {statusLabels[customer.status] || customer.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[#555555]">{customer.deal_value ? formatCurrency(customer.deal_value) : '-'}</td>
                      <td className="px-6 py-4 text-[#555555] text-sm">{customer.source || '-'}</td>
                      <td className="px-6 py-4">
                        <CaretRight size={18} className="text-[#555555]" />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* New Customer Modal */}
      {showNewModal && (
        <NewCustomerModal onClose={() => setShowNewModal(false)} onSuccess={() => { setShowNewModal(false); fetchData(); }} />
      )}
    </div>
  );
};

const NewCustomerModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    product_interest: '',
    status: 'lead',
    deal_value: '',
    source: 'manual'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/customers`, {
        ...formData,
        deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null
      }, { withCredentials: true });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-customer-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg">Add New Customer</h2>
          <button onClick={onClose} className="text-[#555555] hover:text-[#111111]">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#111111] mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#002FA7]"
                required
                data-testid="new-customer-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111111] mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#002FA7]"
                required
                data-testid="new-customer-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#111111] mb-1">Company</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#002FA7]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111111] mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#002FA7]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111111] mb-1">Product Interest</label>
            <select
              value={formData.product_interest}
              onChange={(e) => setFormData({ ...formData, product_interest: e.target.value })}
              className="w-full border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#002FA7]"
            >
              <option value="">Select product...</option>
              <option value="fractionalrisk.ai">fractionalrisk.ai</option>
              <option value="risksurvey.ai">risksurvey.ai</option>
              <option value="riskcoach.ai">riskcoach.ai</option>
              <option value="Other Services">Other Services</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#111111] mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#002FA7]"
              >
                <option value="lead">Lead</option>
                <option value="qualified">Qualified</option>
                <option value="demo_scheduled">Demo Scheduled</option>
                <option value="proposal">Proposal</option>
                <option value="closed_won">Closed Won</option>
                <option value="closed_lost">Closed Lost</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111111] mb-1">Deal Value ($)</label>
              <input
                type="number"
                value={formData.deal_value}
                onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })}
                className="w-full border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#002FA7]"
                placeholder="0"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-[#E5E5E5] py-2 text-[#555555] hover:bg-[#F3F3F3] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#002FA7] text-white py-2 hover:bg-[#001D66] transition-colors disabled:opacity-50"
              data-testid="new-customer-submit"
            >
              {loading ? 'Creating...' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
